import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FileDown, Loader2, CreditCard, CheckCheck, Eraser } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtRp } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { InfoNote, KpiCard, SectionCard, Segmented } from "@/components/common/Cards";
import { SwitchRow } from "@/components/common/Sel";
import { cardName, downloadBlob, groupBy, isReimbursable } from "@/components/cc/ccUtils";
import { useAuth } from "@/lib/auth";

const EMPTY_FORM = { company_name: "", company_address: "", attention: "", title: "", notes: "", requester_name: "" };

export function ReimburseTab({ statements, onCreated }) {
  const { user } = useAuth();
  const ready = useMemo(() => (statements || []).filter((s) => s.status === "ready"), [statements]);
  const [stmtIds, setStmtIds] = useState(null);
  const [mode, setMode] = useState("vendor");
  const [hideClaimed, setHideClaimed] = useState(true);
  const [sel, setSel] = useState(() => new Set());
  const [open, setOpen] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [pdfGroup, setPdfGroup] = useState("category");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (stmtIds === null && ready.length) setStmtIds(new Set(ready.map((s) => s.id)));
  }, [ready, stmtIds]);
  useEffect(() => {
    if (user && !form.requester_name) setForm((f) => ({ ...f, requester_name: user.display_name || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const txs = useMemo(() => {
    const out = [];
    for (const s of ready) {
      if (stmtIds && !stmtIds.has(s.id)) continue;
      for (const t of s.transactions || []) {
        if (!isReimbursable(t)) continue;
        if (hideClaimed && t.report_id) continue;
        out.push({ ...t, card: cardName(s), statement_id: s.id });
      }
    }
    return out;
  }, [ready, stmtIds, hideClaimed]);

  const groups = useMemo(() => groupBy(txs, mode === "vendor" ? "vendor" : "category"), [txs, mode]);
  const visibleIds = useMemo(() => new Set(txs.map((t) => t.id)), [txs]);
  const chosen = txs.filter((t) => sel.has(t.id));
  const chosenTotal = chosen.reduce((a, t) => a + t.amount, 0);
  const allTotal = txs.reduce((a, t) => a + t.amount, 0);
  const maxGroup = Math.max(1, ...groups.map((g) => Math.abs(g.total)));

  const toggleIds = (ids, on) =>
    setSel((prev) => {
      const n = new Set(prev);
      ids.forEach((id) => (on ? n.add(id) : n.delete(id)));
      return n;
    });
  const toggleStmt = (id) =>
    setStmtIds((prev) => {
      const n = new Set(prev || []);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const generate = async () => {
    const ids = chosen.map((t) => t.id).filter((id) => visibleIds.has(id));
    if (!ids.length) return toast.error("Pilih minimal 1 transaksi.");
    if (form.company_name.trim().length < 2) return toast.error("Isi nama perusahaan yang ditagihkan.");
    setBusy(true);
    try {
      const { data } = await api.post("/cc/reports", { ...form, group_by: pdfGroup, transaction_ids: ids });
      await downloadBlob(`/cc/reports/${data.id}/pdf`, `Reimburse-${data.number}.pdf`);
      toast.success(`PDF ${data.number} dibuat · ${fmtRp(data.total)}`);
      setSel(new Set());
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(errMsg(e, "Gagal membuat PDF"));
    } finally {
      setBusy(false);
    }
  };

  if (!ready.length) {
    return (
      <SectionCard testid="cc-reimburse-empty">
        <p className="text-sm text-muted-foreground">Belum ada tagihan yang siap. Tarik dari Gmail atau upload PDF di tab Tagihan terlebih dahulu.</p>
      </SectionCard>
    );
  }

  return (
    <div className="grid xl:grid-cols-[1fr_380px] gap-5 items-start">
      <div className="space-y-5 min-w-0">
        <SectionCard title="Tagihan yang dihitung" description="Klik untuk memasukkan/mengeluarkan kartu dari perhitungan." testid="cc-reimburse-stmts">
          <div className="flex flex-wrap gap-2">
            {ready.map((s) => {
              const on = stmtIds?.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={!!on}
                  onClick={() => toggleStmt(s.id)}
                  className={`press inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm ${on ? "border-primary bg-[hsl(43_28%_9%)] text-primary" : "border-border bg-[hsl(220_12%_8%)] text-muted-foreground hover:text-foreground"}`}
                  data-testid={`cc-reimburse-stmt-${s.id}`}
                >
                  <CreditCard className="h-3.5 w-3.5" /> {cardName(s)} {s.period && <span className="text-[11px] opacity-70">· {s.period}</span>}
                </button>
              );
            })}
          </div>
        </SectionCard>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <KpiCard label="Dipilih untuk reimburse" value={fmtRp(chosenTotal)} accent sub={`${chosen.length} transaksi`} testid="cc-reimburse-selected-total" />
          <KpiCard label="Total bisa diajukan" value={fmtRp(allTotal)} sub={`${txs.length} transaksi (tanpa pembayaran)`} testid="cc-reimburse-all-total" />
          <KpiCard label={mode === "vendor" ? "Vendor" : "Kategori"} value={groups.length} sub={groups[0] ? `Terbesar: ${groups[0].name}` : "–"} testid="cc-reimburse-group-count" />
        </div>

        <SectionCard
          title={mode === "vendor" ? "Per vendor / provider" : "Per kategori penggunaan"}
          description="Centang vendor/kategori untuk memilih semua transaksinya, atau buka untuk memilih per transaksi."
          actions={
            <Segmented
              value={mode}
              onChange={setMode}
              size="sm"
              testid="cc-reimburse-mode"
              options={[
                { value: "vendor", label: "Vendor" },
                { value: "category", label: "Kategori" },
              ]}
            />
          }
          testid="cc-reimburse-groups"
        >
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={() => toggleIds(txs.map((t) => t.id), true)} data-testid="cc-select-all">
              <CheckCheck className="h-3.5 w-3.5 mr-1.5" /> Pilih semua
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSel(new Set())} data-testid="cc-select-none">
              <Eraser className="h-3.5 w-3.5 mr-1.5" /> Kosongkan
            </Button>
            <div className="ml-auto min-w-[260px]">
              <SwitchRow label="Sembunyikan yang sudah diajukan" checked={hideClaimed} onChange={setHideClaimed} testid="cc-hide-claimed" />
            </div>
          </div>

          <div className="divide-y divide-border/70 rounded-lg border border-border">
            {groups.map((g, gi) => {
              const ids = g.items.map((t) => t.id);
              const n = ids.filter((id) => sel.has(id)).length;
              const state = n === 0 ? false : n === ids.length ? true : "indeterminate";
              const isOpen = !!open[`${mode}:${g.name}`];
              return (
                <div key={g.name} data-testid={`cc-group-${gi}`}>
                  <div className={`flex items-center gap-3 px-3 py-2.5 ${state ? "bg-[hsl(43_30%_7%)]" : ""}`}>
                    <Checkbox checked={state} onCheckedChange={(v) => toggleIds(ids, !!v)} aria-label={`Pilih ${g.name}`} data-testid={`cc-group-check-${gi}`} />
                    <button
                      type="button"
                      onClick={() => setOpen((o) => ({ ...o, [`${mode}:${g.name}`]: !isOpen }))}
                      className="flex-1 min-w-0 text-left flex items-center gap-2"
                      data-testid={`cc-group-toggle-${gi}`}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm truncate">{g.name}</span>
                        <span className="block h-1 mt-1.5 rounded-full bg-[hsl(220_10%_14%)] overflow-hidden">
                          <span className="block h-full bg-primary/70 rounded-full" style={{ width: `${(Math.abs(g.total) / maxGroup) * 100}%` }} />
                        </span>
                      </span>
                    </button>
                    <span className="text-[11px] text-muted-foreground num w-20 text-right">{n ? `${n}/` : ""}{ids.length} trx</span>
                    <span className="num text-sm w-32 text-right">{fmtRp(g.total)}</span>
                  </div>
                  {isOpen && (
                    <div className="bg-[hsl(220_12%_6%)] px-3 pb-2">
                      {g.items.map((t) => (
                        <label key={t.id} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0 cursor-pointer pl-7" data-testid={`cc-tx-row-${t.id}`}>
                          <Checkbox checked={sel.has(t.id)} onCheckedChange={(v) => toggleIds([t.id], !!v)} data-testid={`cc-tx-check-${t.id}`} />
                          <span className="num text-[11px] text-muted-foreground w-20 shrink-0">{t.date || "–"}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-xs truncate">{t.description}</span>
                            <span className="block text-[10px] text-muted-foreground truncate">
                              {t.card} · {mode === "vendor" ? t.category : t.vendor}
                              {t.report_number && <span className="text-primary"> · sudah di {t.report_number}</span>}
                            </span>
                          </span>
                          <span className={`num text-xs whitespace-nowrap ${t.amount < 0 ? "text-[hsl(160_55%_56%)]" : ""}`}>{fmtRp(t.amount)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {!groups.length && <div className="p-4 text-sm text-muted-foreground">Tidak ada transaksi untuk dipilih.</div>}
          </div>
        </SectionCard>
      </div>

      <div className="xl:sticky xl:top-4">
        <SectionCard title="Buat PDF reimburse" eyebrow="Ditagihkan ke perusahaan" testid="cc-reimburse-form">
          <div className="space-y-3">
            <Field label="Nama perusahaan *" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} placeholder="PT ..." testid="cc-form-company" />
            <div>
              <Label className="text-xs text-muted-foreground">Alamat perusahaan</Label>
              <Textarea value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} rows={2} className="mt-1 bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm" data-testid="cc-form-address" />
            </div>
            <Field label="Up. (attention)" value={form.attention} onChange={(v) => setForm({ ...form, attention: v })} placeholder="mis. Bagian Finance" testid="cc-form-attention" />
            <Field label="Judul" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="mis. Reimburse kartu kredit Oktober 2026" testid="cc-form-title" />
            <Field label="Nama pengaju" value={form.requester_name} onChange={(v) => setForm({ ...form, requester_name: v })} placeholder={user?.email} testid="cc-form-requester" />
            <div>
              <Label className="text-xs text-muted-foreground">Catatan</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)] text-sm" data-testid="cc-form-notes" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Kelompokkan isi PDF per</Label>
              <div className="mt-1">
                <Segmented value={pdfGroup} onChange={setPdfGroup} size="sm" testid="cc-form-groupby" options={[{ value: "category", label: "Kategori" }, { value: "vendor", label: "Vendor" }]} />
              </div>
            </div>
            <div className="rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] p-3 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{chosen.length} transaksi dipilih</span>
              <span className="num text-lg text-primary" data-testid="cc-form-total">{fmtRp(chosenTotal)}</span>
            </div>
            <Button onClick={generate} disabled={busy || !chosen.length} className="w-full h-11 press" data-testid="cc-generate-pdf">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Generate PDF reimburse
            </Button>
            <InfoNote>Transaksi yang sudah masuk laporan akan ditandai agar tidak diajukan dua kali. Pembayaran tagihan otomatis dikecualikan; refund mengurangi total.</InfoNote>
            {chosen.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {groupBy(chosen, pdfGroup).slice(0, 8).map((g) => (
                  <Badge key={g.name} variant="outline" className="border-border text-[10px]">{g.name}: {fmtRp(g.total)}</Badge>
                ))}
              </div>
            )}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 h-10 bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)]" data-testid={testid} />
    </div>
  );
}
